import React, { useState, useMemo } from "react";
import {
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Plus,
  X,
  Users,
  Send,
  AlertTriangle,
  ShieldCheck,
  CheckCircle2,
  Search,
  RefreshCw,
  Info,
  Sparkles,
  ArrowRight,
  Clock,
} from "lucide-react";
import HeaderBar from "../students/components/headerBar";
import {
  useVerificationWalletQuery,
  useStaffWalletsQuery,
  useAllocateFundsMutation,
  useRequestAllocationMutation,
} from "../../hooks/queries/useVerificationQueries";
import { useAuthicatedUser } from "react-redux-django-auth";
import { useToast } from "../../hooks/otherHooks/useToast";

// ─── SuperAdmin Master Top-up Modal ──────────────────────────────────────────
const TopUpModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (data: any) => void;
}) => {
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const allocateMutation = useAllocateFundsMutation();
  const { addToast } = useToast();
  const PRESETS = [5000, 10000, 25000, 50000];

  const handleTopUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      addToast({ message: "Please enter a valid positive amount.", bg: "warning" });
      return;
    }

    allocateMutation.mutate(
      { amount: parsedAmount, memo: memo.trim() || undefined },
      {
        onSuccess: (data) => {
          addToast({
            message: data.message || `₦${parsedAmount.toLocaleString()} added to master wallet.`,
            bg: "success",
          });
          onSuccess(data);
        },
        onError: (err: any) => {
          addToast({ message: err.message || "Failed to top up wallet.", bg: "danger" });
        },
      }
    );
  };

  return (
    <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-bg-black/50 cht-backdrop-blur-sm cht-p-4 cht-animate-in cht-fade-in cht-duration-200">
      <div className="cht-bg-white cht-rounded-squircle cht-shadow-2xl cht-border cht-border-slate-200/80 cht-w-full cht-max-w-md cht-p-6 cht-relative cht-space-y-5">
        <div className="cht-flex cht-justify-between cht-items-center cht-border-b cht-border-gray-100 cht-pb-4">
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
              <Plus size={18} />
            </div>
            <div>
              <h3 className="cht-text-base cht-font-bold cht-text-gray-900">Top Up Master Wallet</h3>
              <p className="cht-text-xs cht-text-gray-500">Add operational capital to the central balance</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-lg hover:cht-bg-gray-100 cht-transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleTopUp} className="cht-space-y-4">
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-2">
              Select Preset Amount
            </label>
            <div className="cht-grid cht-grid-cols-4 cht-gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(String(p))}
                  className={`cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-border cht-transition-all ${
                    amount === String(p)
                      ? "cht-bg-[#3e57da] cht-text-white cht-border-[#3e57da] cht-shadow-sm"
                      : "cht-bg-gray-50 cht-text-gray-700 cht-border-gray-200 hover:cht-bg-white hover:cht-border-[#3e57da]/40"
                  }`}
                >
                  ₦{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Or Enter Custom Amount (₦)
            </label>
            <div className="cht-relative">
              <span className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-500 cht-font-bold cht-text-sm">
                ₦
              </span>
              <input
                type="number"
                min="100"
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 20000"
                className="cht-w-full cht-pl-8 cht-pr-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-font-semibold focus:cht-border-[#3e57da] cht-outline-none cht-transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Memo / Purpose <span className="cht-text-gray-400 cht-font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="e.g. November Verification Operating Float"
              className="cht-w-full cht-px-3.5 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-bg-gray-50/50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none cht-transition-colors"
            />
          </div>

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cht-px-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-600 hover:cht-bg-gray-50 cht-transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={allocateMutation.isPending}
              className="cht-flex cht-items-center cht-gap-2 cht-px-5 cht-py-2.5 cht-bg-[#3e57da] cht-text-white cht-rounded-xl cht-text-xs cht-font-bold hover:cht-bg-[#3248c0] cht-transition-colors disabled:cht-opacity-60 cht-shadow-sm"
            >
              {allocateMutation.isPending ? (
                <>
                  <RefreshCw size={14} className="cht-animate-spin" />
                  <span>Processing…</span>
                </>
              ) : (
                <>
                  <Plus size={14} />
                  <span>Fund Wallet</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── SuperAdmin Allocation Modal (Allocate Funds to Admin) ───────────────────
const AllocateFundsModal = ({
  staffList,
  initialStaff = null,
  onClose,
  onSuccess,
}: {
  staffList: any[];
  initialStaff?: any | null;
  onClose: () => void;
  onSuccess: (data: any) => void;
}) => {
  const [selectedStaffId, setSelectedStaffId] = useState<string>(
    initialStaff ? String(initialStaff.id) : staffList[0] ? String(staffList[0].id) : ""
  );
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const allocateMutation = useAllocateFundsMutation();
  const { addToast } = useToast();
  const PRESETS = [2000, 5000, 10000, 20000];

  const selectedStaff = useMemo(
    () => staffList.find((s) => String(s.id) === selectedStaffId) || null,
    [staffList, selectedStaffId]
  );

  const currentBalance = selectedStaff ? parseFloat(selectedStaff.balance || 0) : 0;
  const parsedAmount = parseFloat(amount) || 0;
  const projectedBalance = currentBalance + parsedAmount;

  const handleAllocate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStaffId) {
      addToast({ message: "Please select an admin staff member.", bg: "warning" });
      return;
    }
    if (!amount || parsedAmount <= 0) {
      addToast({ message: "Please enter a valid positive allocation amount.", bg: "warning" });
      return;
    }

    allocateMutation.mutate(
      {
        target_user_id: parseInt(selectedStaffId, 10),
        amount: parsedAmount,
        memo: memo.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          addToast({
            message:
              data.message ||
              `₦${parsedAmount.toLocaleString()} successfully allocated to ${
                selectedStaff?.name || "staff member"
              }!`,
            bg: "success",
          });
          onSuccess(data);
        },
        onError: (err: any) => {
          addToast({ message: err.message || "Failed to allocate funds.", bg: "danger" });
        },
      }
    );
  };

  return (
    <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-bg-black/50 cht-backdrop-blur-sm cht-p-4 cht-animate-in cht-fade-in cht-duration-200">
      <div className="cht-bg-white cht-rounded-squircle cht-shadow-2xl cht-border cht-border-slate-200/80 cht-w-full cht-max-w-lg cht-p-6 cht-relative cht-space-y-5">
        <div className="cht-flex cht-justify-between cht-items-center cht-border-b cht-border-gray-100 cht-pb-4">
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-emerald-50 cht-text-emerald-600 cht-flex cht-items-center cht-justify-center">
              <Send size={18} />
            </div>
            <div>
              <h3 className="cht-text-base cht-font-bold cht-text-gray-900">
                Allocate Funds to Admin
              </h3>
              <p className="cht-text-xs cht-text-gray-500">
                Credit a staff member&apos;s verification wallet from administration
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-lg hover:cht-bg-gray-100 cht-transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleAllocate} className="cht-space-y-4">
          {/* Staff Member Selection */}
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Select Admin / Staff Recipient <span className="cht-text-rose-500">*</span>
            </label>
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              className="cht-w-full cht-px-3.5 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-font-medium cht-bg-gray-50/50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none"
              required
            >
              <option value="" disabled>
                -- Choose Staff Member --
              </option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.email}) — Balance: ₦{parseFloat(s.balance || 0).toLocaleString()}
                </option>
              ))}
            </select>
          </div>

          {/* Current & Projected Balance Card */}
          {selectedStaff && (
            <div className="cht-p-3.5 cht-bg-slate-50 cht-rounded-xl cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-justify-between cht-text-xs">
              <div>
                <span className="cht-text-gray-500 cht-block">Current Balance</span>
                <span className="cht-font-bold cht-text-gray-800">
                  ₦{currentBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>
              <ArrowRight size={16} className="cht-text-gray-400" />
              <div className="cht-text-right">
                <span className="cht-text-gray-500 cht-block">Projected Balance</span>
                <span className="cht-font-bold cht-text-emerald-600">
                  ₦{projectedBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          {/* Preset Buttons */}
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Preset Allocation Amounts
            </label>
            <div className="cht-grid cht-grid-cols-4 cht-gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(String(p))}
                  className={`cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-border cht-transition-all ${
                    amount === String(p)
                      ? "cht-bg-emerald-600 cht-text-white cht-border-emerald-600 cht-shadow-sm"
                      : "cht-bg-gray-50 cht-text-gray-700 cht-border-gray-200 hover:cht-bg-white hover:cht-border-emerald-500/50"
                  }`}
                >
                  +₦{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Custom Amount to Allocate (₦)
            </label>
            <div className="cht-relative">
              <span className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-500 cht-font-bold cht-text-sm">
                ₦
              </span>
              <input
                type="number"
                min="100"
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 5000"
                className="cht-w-full cht-pl-8 cht-pr-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-font-semibold focus:cht-border-emerald-600 cht-outline-none cht-transition-colors"
                required
              />
            </div>
          </div>

          {/* Memo / Notes */}
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Allocation Memo / Purpose <span className="cht-text-gray-400 cht-font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="e.g. Weekly operational float for customer NIN/BVN verifications"
              className="cht-w-full cht-px-3.5 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-bg-gray-50/50 focus:cht-bg-white focus:cht-border-emerald-600 cht-outline-none cht-transition-colors"
            />
          </div>

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cht-px-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-600 hover:cht-bg-gray-50 cht-transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={allocateMutation.isPending}
              className="cht-flex cht-items-center cht-gap-2 cht-px-5 cht-py-2.5 cht-bg-emerald-600 cht-text-white cht-rounded-xl cht-text-xs cht-font-bold hover:cht-bg-emerald-700 cht-transition-colors disabled:cht-opacity-60 cht-shadow-sm"
            >
              {allocateMutation.isPending ? (
                <>
                  <RefreshCw size={14} className="cht-animate-spin" />
                  <span>Allocating…</span>
                </>
              ) : (
                <>
                  <Send size={14} />
                  <span>Confirm Allocation</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Admin Request Allocation Modal ──────────────────────────────────────────
const RequestAllocationModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const requestMutation = useRequestAllocationMutation();
  const { addToast } = useToast();
  const PRESETS = [2000, 5000, 10000];

  const handleRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      addToast({ message: "Please specify a valid requested amount.", bg: "warning" });
      return;
    }

    requestMutation.mutate(
      { amount: parsed, reason: reason.trim() || undefined },
      {
        onSuccess: (data) => {
          addToast({
            message:
              data.message ||
              `Allocation request for ₦${parsed.toLocaleString()} submitted to Super Administrators.`,
            bg: "success",
          });
          onSuccess();
        },
        onError: (err: any) => {
          addToast({
            message: err.message || "Failed to submit request.",
            bg: "danger",
          });
        },
      }
    );
  };

  return (
    <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-bg-black/50 cht-backdrop-blur-sm cht-p-4 cht-animate-in cht-fade-in cht-duration-200">
      <div className="cht-bg-white cht-rounded-squircle cht-shadow-2xl cht-border cht-border-slate-200/80 cht-w-full cht-max-w-md cht-p-6 cht-relative cht-space-y-5">
        <div className="cht-flex cht-justify-between cht-items-center cht-border-b cht-border-gray-100 cht-pb-4">
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-amber-50 cht-text-amber-600 cht-flex cht-items-center cht-justify-center">
              <Clock size={18} />
            </div>
            <div>
              <h3 className="cht-text-base cht-font-bold cht-text-gray-900">
                Request Wallet Allocation
              </h3>
              <p className="cht-text-xs cht-text-gray-500">
                Notify Super Administrators to credit your verification wallet
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-lg hover:cht-bg-gray-100 cht-transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleRequest} className="cht-space-y-4">
          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Requested Amount (₦)
            </label>
            <div className="cht-grid cht-grid-cols-3 cht-gap-2 cht-mb-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(String(p))}
                  className={`cht-py-2 cht-rounded-xl cht-text-xs cht-font-bold cht-border cht-transition-all ${
                    amount === String(p)
                      ? "cht-bg-[#3e57da] cht-text-white cht-border-[#3e57da]"
                      : "cht-bg-gray-50 cht-text-gray-700 cht-border-gray-200 hover:cht-bg-white hover:cht-border-[#3e57da]/40"
                  }`}
                >
                  ₦{p.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="cht-relative">
              <span className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-500 cht-font-bold cht-text-sm">
                ₦
              </span>
              <input
                type="number"
                min="500"
                step="500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Or enter custom amount"
                className="cht-w-full cht-pl-8 cht-pr-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-font-semibold focus:cht-border-[#3e57da] cht-outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
              Reason / Operational Requirement <span className="cht-text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Running low on funds for scheduled customer NIN registrations and identity slip downloads."
              className="cht-w-full cht-px-3.5 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-bg-gray-50/50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none"
              required
            />
          </div>

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cht-px-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-600 hover:cht-bg-gray-50 cht-transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={requestMutation.isPending}
              className="cht-flex cht-items-center cht-gap-2 cht-px-5 cht-py-2.5 cht-bg-[#3e57da] cht-text-white cht-rounded-xl cht-text-xs cht-font-bold hover:cht-bg-[#3248c0] cht-transition-colors disabled:cht-opacity-60 cht-shadow-sm"
            >
              {requestMutation.isPending ? (
                <>
                  <RefreshCw size={14} className="cht-animate-spin" />
                  <span>Submitting…</span>
                </>
              ) : (
                <>
                  <Send size={14} />
                  <span>Send Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Transaction Row ──────────────────────────────────────────────────────────
const TxRow = ({ tx }: { tx: any }) => {
  const isDeposit = tx.transaction_type === "DEPOSIT";
  return (
    <tr
      className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/50 cht-transition-colors"
      style={{ height: 56 }}
    >
      <td className="cht-px-5 cht-py-3">
        <div className="cht-flex cht-items-center cht-gap-3">
          <div
            className={`cht-p-2 cht-rounded-xl cht-shrink-0 ${
              isDeposit
                ? "cht-bg-emerald-50 cht-text-emerald-600"
                : "cht-bg-rose-50 cht-text-rose-500"
            }`}
          >
            {isDeposit ? <ArrowUpCircle size={16} /> : <ArrowDownCircle size={16} />}
          </div>
          <div>
            <span className="cht-text-xs cht-font-bold cht-text-gray-900 cht-block">
              {tx.service_name || "Verification Transaction"}
            </span>
            {tx.reference && (
              <span className="cht-text-[10px] cht-font-mono cht-text-gray-400">
                Ref: {String(tx.reference).slice(0, 8)}…
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="cht-px-5 cht-py-3 cht-text-xs cht-text-gray-500">
        {new Date(tx.created_at).toLocaleString()}
      </td>
      <td className="cht-px-5 cht-py-3">
        <span
          className={`cht-inline-flex cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-bold ${
            tx.status === "SUCCESS"
              ? "cht-bg-emerald-50 cht-text-emerald-700"
              : tx.status === "PENDING"
              ? "cht-bg-amber-50 cht-text-amber-700"
              : "cht-bg-rose-50 cht-text-rose-700"
          }`}
        >
          {tx.status}
        </span>
      </td>
      <td
        className={`cht-px-5 cht-py-3 cht-text-xs cht-font-black cht-text-right ${
          isDeposit ? "cht-text-emerald-600" : "cht-text-rose-500"
        }`}
      >
        {isDeposit ? "+" : "−"}₦
        {parseFloat(tx.amount || 0).toLocaleString("en-NG", {
          minimumFractionDigits: 2,
        })}
      </td>
    </tr>
  );
};

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <tr className="cht-border-b cht-border-gray-100 cht-animate-pulse" style={{ height: 56 }}>
    {[1, 2, 3, 4].map((i) => (
      <td key={i} className="cht-px-5 cht-py-3">
        <div className="cht-h-3 cht-bg-gray-200 cht-rounded cht-w-3/4" />
      </td>
    ))}
  </tr>
);

// ─── Main Wallet Page ─────────────────────────────────────────────────────────
const WalletPage: React.FC = () => {
  const { currentUser } = useAuthicatedUser();
  const userData = (currentUser as any)?.user || (currentUser as any)?.data?.user || currentUser;
  const isSuperAdmin = Boolean(
    userData?.is_superuser ||
      userData?.role === "SUPERADMIN" ||
      (Array.isArray(userData?.roles) && userData.roles.includes("SUPERADMIN"))
  );

  const { data: walletData, isLoading: loading } = useVerificationWalletQuery();
  const { data: staffWallets = [], isLoading: staffLoading } = useStaffWalletsQuery(isSuperAdmin);

  const wallet = walletData?.wallet || null;
  const transactions = walletData?.transactions || [];

  const [showTopUp, setShowTopUp] = useState<boolean>(false);
  const [showAllocateModal, setShowAllocateModal] = useState<boolean>(false);
  const [preSelectedStaff, setPreSelectedStaff] = useState<any | null>(null);
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false);
  const [staffSearch, setStaffSearch] = useState<string>("");

  const balanceNumber = parseFloat(wallet?.balance ?? 0);
  const isLowBalance = balanceNumber < 1000;

  // Filter staff list by search query
  const filteredStaff = useMemo(() => {
    if (!staffSearch.trim()) return staffWallets;
    const q = staffSearch.toLowerCase();
    return staffWallets.filter(
      (s: any) =>
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.role?.toLowerCase().includes(q)
    );
  }, [staffWallets, staffSearch]);

  const totalStaffAllocated = useMemo(() => {
    return staffWallets.reduce(
      (acc: number, cur: any) => acc + parseFloat(cur.balance || 0),
      0
    );
  }, [staffWallets]);

  const totalStaffVerifications = useMemo(() => {
    return staffWallets.reduce(
      (acc: number, cur: any) => acc + (cur.total_verifications || 0),
      0
    );
  }, [staffWallets]);

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-500">
      <HeaderBar pageHeader="Verification Wallet & Funds" />

      <main className="cht-container cht-mx-auto cht-mb-16 cht-space-y-8">
        {/* Balance Hero Card */}
        <div className="cht-bg-gradient-to-br cht-from-[#1e293b] cht-via-indigo-950 cht-to-[#1e293b] cht-rounded-[28px] cht-p-6 sm:cht-p-8 cht-text-white cht-relative cht-overflow-hidden cht-shadow-xl cht-border cht-border-indigo-900/40">
          <div className="cht-absolute -cht-top-12 -cht-right-12 cht-w-60 cht-h-60 cht-bg-[#3e57da]/20 cht-rounded-full cht-blur-3xl cht-pointer-events-none" />
          <div className="cht-absolute -cht-bottom-12 -cht-left-12 cht-w-60 cht-h-60 cht-bg-indigo-600/10 cht-rounded-full cht-blur-3xl cht-pointer-events-none" />

          <div className="cht-relative cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-6">
            <div className="cht-space-y-3">
              <div className="cht-inline-flex cht-items-center cht-gap-2 cht-px-3 cht-py-1 cht-rounded-full cht-bg-white/10 cht-border cht-border-white/15 cht-text-xs cht-font-semibold cht-text-indigo-200">
                <ShieldCheck size={13} className="cht-text-indigo-300" />
                <span>
                  {isSuperAdmin
                    ? "Company Primary Verification Account"
                    : "Staff Operational Verification Account"}
                </span>
              </div>

              <div>
                <span className="cht-text-xs cht-text-slate-300 cht-font-medium cht-block cht-mb-1">
                  Available Wallet Balance
                </span>
                {loading ? (
                  <div className="cht-h-12 cht-w-48 cht-bg-white/20 cht-rounded-2xl cht-animate-pulse" />
                ) : (
                  <h2 className="cht-text-3xl sm:cht-text-4xl cht-font-black cht-tracking-tight">
                    ₦
                    {balanceNumber.toLocaleString("en-NG", {
                      minimumFractionDigits: 2,
                    })}
                  </h2>
                )}
              </div>

              <p className="cht-text-xs cht-text-slate-300 cht-max-w-xl cht-leading-relaxed">
                {isSuperAdmin
                  ? "Central capital pool for identity verification services (NIN, BVN, CAC, TIN, etc.). Allocate funds to staff accounts below."
                  : "Your balance is funded by Super Administrators. Funds are automatically deducted when running verification requests."}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
              {isSuperAdmin ? (
                <>
                  <button
                    onClick={() => setShowTopUp(true)}
                    className="cht-inline-flex cht-items-center cht-gap-2 cht-bg-[#3e57da] hover:cht-bg-[#3248c0] cht-text-white cht-font-bold cht-text-xs cht-px-4 cht-py-3 cht-rounded-xl cht-transition-all cht-shadow-md"
                  >
                    <Plus size={16} /> Top Up My Balance
                  </button>
                  <button
                    onClick={() => {
                      setPreSelectedStaff(null);
                      setShowAllocateModal(true);
                    }}
                    className="cht-inline-flex cht-items-center cht-gap-2 cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-white cht-font-bold cht-text-xs cht-px-4 cht-py-3 cht-rounded-xl cht-transition-all cht-shadow-md"
                  >
                    <Send size={15} /> Allocate Funds to Admin
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowRequestModal(true)}
                  className="cht-inline-flex cht-items-center cht-gap-2 cht-bg-[#3e57da] hover:cht-bg-[#3248c0] cht-text-white cht-font-bold cht-text-xs cht-px-4 cht-py-3 cht-rounded-xl cht-transition-all cht-shadow-md"
                >
                  <Send size={15} /> Request Allocation
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Admin Staff Info & Low Balance Warning */}
        {!isSuperAdmin && (
          <div className="cht-space-y-3">
            {isLowBalance && (
              <div className="cht-p-4 cht-rounded-2xl cht-bg-amber-50 cht-border cht-border-amber-200/80 cht-flex cht-items-start cht-gap-3 cht-text-xs cht-text-amber-900">
                <AlertTriangle size={18} className="cht-text-amber-600 cht-shrink-0 cht-mt-0.5" />
                <div className="cht-space-y-1">
                  <div className="cht-font-bold">Low Verification Balance Alert</div>
                  <p className="cht-leading-relaxed">
                    Your balance is below ₦1,000.00. Verification requests will fail if funds are
                    insufficient. Please request an allocation from your Super Administrator.
                  </p>
                </div>
              </div>
            )}

            <div className="cht-p-4 cht-rounded-2xl cht-bg-indigo-50/60 cht-border cht-border-indigo-100 cht-flex cht-items-start cht-gap-3 cht-text-xs cht-text-indigo-900">
              <Info size={18} className="cht-text-[#3e57da] cht-shrink-0 cht-mt-0.5" />
              <div className="cht-space-y-1">
                <div className="cht-font-bold">Managed Operational Float</div>
                <p className="cht-leading-relaxed">
                  Verification transactions are audited per account. Each time you execute an NIN,
                  BVN, or business search, your wallet is debited according to the service price.
                  Any system failure automatically issues an immediate refund.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SuperAdmin Staff Wallets Management Matrix */}
        {isSuperAdmin && (
          <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-border cht-border-slate-200/80 cht-overflow-hidden cht-space-y-4">
            <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-4">
              <div>
                <div className="cht-flex cht-items-center cht-gap-2">
                  <Users size={18} className="cht-text-[#3e57da]" />
                  <h3 className="cht-text-base cht-font-black cht-text-gray-900">
                    Staff & Admin Verification Wallets
                  </h3>
                </div>
                <p className="cht-text-xs cht-text-gray-500 cht-mt-0.5">
                  Monitor operational balances and allocate verification budget to staff on demand
                </p>
              </div>

              {/* Quick Metrics Chips */}
              <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                <div className="cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200 cht-text-xs">
                  <span className="cht-text-gray-400">Total Staff: </span>
                  <span className="cht-font-bold cht-text-gray-800">{staffWallets.length}</span>
                </div>
                <div className="cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-text-xs">
                  <span className="cht-text-emerald-700">Allocated Pool: </span>
                  <span className="cht-font-bold cht-text-emerald-800">
                    ₦{totalStaffAllocated.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Filter Search */}
            <div className="cht-px-6">
              <div className="cht-relative cht-max-w-md">
                <Search
                  size={15}
                  className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
                />
                <input
                  type="text"
                  value={staffSearch}
                  onChange={(e) => setStaffSearch(e.target.value)}
                  placeholder="Filter staff by name or email…"
                  className="cht-w-full cht-pl-9 cht-pr-3.5 cht-py-2 cht-text-xs cht-bg-gray-50/50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none"
                />
              </div>
            </div>

            {/* Table */}
            <div className="cht-overflow-x-auto">
              <table className="cht-w-full">
                <thead>
                  <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/70">
                    <th className="cht-px-6 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                      Staff Member
                    </th>
                    <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                      Role
                    </th>
                    <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                      Wallet Balance
                    </th>
                    <th className="cht-px-5 cht-py-3.5 cht-text-center cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                      Verifications
                    </th>
                    <th className="cht-px-6 cht-py-3.5 cht-text-right cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {staffLoading ? (
                    Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                  ) : filteredStaff.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="cht-text-center cht-py-12 cht-text-xs cht-text-gray-400 font-medium"
                      >
                        No staff members found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((staff: any) => {
                      const staffBal = parseFloat(staff.balance || 0);
                      return (
                        <tr
                          key={staff.id}
                          className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/50 cht-transition-colors"
                          style={{ height: 56 }}
                        >
                          <td className="cht-px-6 cht-py-3">
                            <div className="cht-flex cht-items-center cht-gap-3">
                              <div className="cht-w-8 cht-h-8 cht-rounded-full cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-font-black cht-text-xs">
                                {staff.name
                                  ?.split(" ")
                                  .map((n: string) => n[0])
                                  .slice(0, 2)
                                  .join("")
                                  .toUpperCase() || "ST"}
                              </div>
                              <div>
                                <span className="cht-text-xs cht-font-bold cht-text-gray-900 cht-block">
                                  {staff.name}
                                </span>
                                <span className="cht-text-[11px] cht-text-gray-400">
                                  {staff.email}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="cht-px-5 cht-py-3">
                            <span
                              className={`cht-inline-flex cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold ${
                                staff.is_superuser || staff.role === "SUPERADMIN"
                                  ? "cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200"
                                  : "cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200"
                              }`}
                            >
                              {staff.role || "ADMIN"}
                            </span>
                          </td>
                          <td className="cht-px-5 cht-py-3">
                            <span
                              className={`cht-text-xs cht-font-extrabold ${
                                staffBal >= 2000
                                  ? "cht-text-emerald-600"
                                  : staffBal > 0
                                  ? "cht-text-amber-600"
                                  : "cht-text-rose-500"
                              }`}
                            >
                              ₦
                              {staffBal.toLocaleString("en-NG", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </td>
                          <td className="cht-px-5 cht-py-3 cht-text-center">
                            <span className="cht-text-xs cht-font-bold cht-text-gray-700">
                              {staff.total_verifications || 0}
                            </span>
                          </td>
                          <td className="cht-px-6 cht-py-3 cht-text-right">
                            <button
                              onClick={() => {
                                setPreSelectedStaff(staff);
                                setShowAllocateModal(true);
                              }}
                              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-rounded-lg cht-bg-emerald-50 hover:cht-bg-emerald-100 cht-text-emerald-700 cht-text-xs cht-font-bold cht-transition-colors"
                            >
                              <Send size={12} />
                              <span>Allocate</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transaction History Section */}
        <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-border cht-border-slate-200/80 cht-overflow-hidden">
          <div className="cht-px-6 cht-py-4 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <div>
              <h3 className="cht-font-bold cht-text-gray-900">Wallet Audit & Transaction Log</h3>
              <p className="cht-text-xs cht-text-gray-500">
                Detailed record of allocations, top-ups, and service verifications
              </p>
            </div>
            <span className="cht-text-xs cht-font-semibold cht-text-gray-500">
              {transactions.length} record(s)
            </span>
          </div>

          <div className="cht-overflow-x-auto">
            <table className="cht-w-full">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/70">
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                    Transaction / Description
                  </th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                    Date & Time
                  </th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                    Status
                  </th>
                  <th className="cht-px-5 cht-py-3.5 cht-text-right cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">
                    Amount (₦)
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                ) : transactions.length > 0 ? (
                  transactions.map((tx: any, i: number) => <TxRow key={tx.id || i} tx={tx} />)
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="cht-text-center cht-py-16 cht-text-gray-400 cht-text-xs font-medium"
                    >
                      No transactions recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* MODALS */}
      {showTopUp && (
        <TopUpModal onClose={() => setShowTopUp(false)} onSuccess={() => setShowTopUp(false)} />
      )}

      {showAllocateModal && (
        <AllocateFundsModal
          staffList={staffWallets}
          initialStaff={preSelectedStaff}
          onClose={() => {
            setShowAllocateModal(false);
            setPreSelectedStaff(null);
          }}
          onSuccess={() => {
            setShowAllocateModal(false);
            setPreSelectedStaff(null);
          }}
        />
      )}

      {showRequestModal && (
        <RequestAllocationModal
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => setShowRequestModal(false)}
        />
      )}
    </section>
  );
};

export default WalletPage;
